import { createContext, useContext, useEffect, useState } from 'react';
import client, { tokens, viewAs, endViewInTab, onViewAsPath } from '../api/client';
import * as authApi from '../api/auth';
import { unsubscribeFromPush } from '../push';
import { resetPerformanceScopes } from '../hooks/usePerformanceScopes';
import { resetUnreadCount } from '../components/NotifBell';

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

// The view pages (and a tab whose view has ended) show no app at all, and the
// sign-in this browser holds is a different agent's: it is neither loaded nor
// sent anywhere from them.
const parked = () => !viewAs.active && (onViewAsPath() || viewAs.ended);

export function AuthProvider({ children }) {
  const [agent, setAgent] = useState(() => (parked() ? null : tokens.agent));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // hydrate profile if we have a session
    (async () => {
      if (tokens.access && !parked()) {
        try {
          const res = await authApi.listMe();
          const me = res?.data ?? res;
          setAgent(me);
          tokens.set({ agent: me });
        } catch {
          /* interceptor handles 401 → login */
        }
      }
      setReady(true);
    })();
  }, []);

  const login = async (email, password) => {
    const res = await authApi.createAuthLogin({ email, password });
    const payload = res?.data ?? res;
    tokens.set({ accessToken: payload.accessToken, refreshToken: payload.refreshToken, agent: payload.agent });
    setAgent(payload.agent);
    return payload.agent;
  };

  const logout = async () => {
    // Signing out of a view ends the view; the agent's own sessions are untouched.
    if (viewAs.active) {
      try { await client.post('/api/agent/auth/view-as/end'); } catch { /* ends on expiry anyway */ }
      endViewInTab();
      return;
    }
    await unsubscribeFromPush(); // stop pushing to this device (needs the session, so before clear)
    try { await authApi.createAuthLogout({ refresh_token: tokens.refresh }); } catch { /* best effort */ }
    tokens.clear();
    // Which downline/branches you can see is cached for the session — the next
    // agent to sign in on this device must not inherit it. The unread badge is
    // cached the same way, and now fetched only once per launch, so it has to be
    // cleared here too or it would show the previous agent's count until the app
    // is fully restarted.
    resetPerformanceScopes();
    resetUnreadCount();
    setAgent(null);
  };

  return (
    <AuthCtx.Provider value={{ agent, ready, isAuthed: !!agent && !!tokens.access, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}
